import { formatNumber } from "accounting"
import { Badge, Dropdown, Flex, Space, Switch, Tag, Typography } from "antd"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import ButtonComponent from "../../../../Components/Button"
import { collapseRowCell } from "../../../../Components/Table/baseColumns"
import { titleSortFilters } from "../../../../Components/titleSortFilters"
import Icon from "../../../../Icon"
import { formatStoredPhone } from "../../../../Utils/phone/readPhone"
import {
  alertsColumnIconMap,
  dateFormat,
  statusTextMap,
  tagColorMap,
  tagMap,
  usersColumnIconMap
} from "../../../../Utils/utils"

const { Text, Link, Paragraph } = Typography

dayjs.extend(utc)

const getStatusTag = (_) => {
  if (!_) return "—"
  return (
    <Tag
      color={tagMap[_?.toLowerCase()] || "default"}
      key={_}
      style={{ margin: 0 }}
      className={"simple large"}
    >
      <Space>
        <Badge
          status={tagMap[_?.toLowerCase()] || "default"}
          color={tagColorMap[_?.toLowerCase()]}
        />
        <Text>{statusTextMap[_] || _}</Text>
      </Space>
    </Tag>
  )
}

const onCell = (record, index, userIdList, data) => {
  const key = "id"
  const countDuplicates = userIdList?.filter((id) => id === record?.[key])?.length
  return collapseRowCell({
    value: record?.[key],
    index,
    prevValue: index ? data[index - 1]?.[key] : null,
    countDuplicates
  })
}

export const getColumns = ({ data, onSelectRow }) => {
  const userIdList = [...data]?.map(({ id }) => id)

  return {
    status: {
      key: "status",
      title: (state) =>
        titleSortFilters({
          title: "Status",
          icon: alertsColumnIconMap["status"],
          stateData: state,
          key: "status"
        }),
      dataIndex: "status",
      fixed: "left",
      width: 120,
      render: getStatusTag,
      onCell: (record, index) => onCell(record, index, userIdList, data)
    },
    user_full_name: {
      key: "user_full_name",
      title: (state) =>
        titleSortFilters({
          title: "User full name",
          icon: alertsColumnIconMap["user_full_name"],
          stateData: state,
          key: "user_full_name"
        }),
      dataIndex: "user_full_name",
      fixed: "left",
      onCell: (record, index) => onCell(record, index, userIdList, data),
      width: 185
    },
    user_email: {
      key: "user_email",
      title: (state) =>
        titleSortFilters({
          title: "User email",
          icon: alertsColumnIconMap["user_email"],
          stateData: state,
          key: "user_email"
        }),
      dataIndex: "user_email",
      onCell: (record, index) => onCell(record, index, userIdList, data)
      //width: 185,
    },
    user_phone: {
      key: "user_phone",
      title: (state) =>
        titleSortFilters({
          title: "Phone number",
          stateData: state,
          key: "user_phone"
        }),
      dataIndex: "user_phone",
      width: 185,
      render: (_) => formatStoredPhone(_) || "—",
      onCell: (record, index) => onCell(record, index, userIdList, data)
    },
    created_timestamp: {
      key: "created_timestamp",
      title: (state) =>
        titleSortFilters({
          title: "Creation date",
          icon: alertsColumnIconMap["created_timestamp"],
          stateData: state,
          key: "created_timestamp"
        }),
      dataIndex: "created_timestamp",
      width: 185,
      render: (_) => (
        <Space direction="vertical" size={4}>
          <Text>{dayjs(_).format(dateFormat)}</Text>
          <Text type="secondary">{dayjs(_).format("h:mm A [(UTC)]")}</Text>
        </Space>
      ),
      onCell: (record, index) => onCell(record, index, userIdList, data)
    },
    locked: {
      key: "locked",
      title: (state) =>
        titleSortFilters({
          title: "Locked",
          stateData: state,
          key: "locked",
          icon: usersColumnIconMap["locked"]
        }),
      dataIndex: "locked",
      minWidth: 110,
      render: (_) => (_ ? "Yes" : "No"),
      onCell: (record, index) => onCell(record, index, userIdList, data)
    },
    last_lock_at: {
      key: "last_lock_at",
      title: (state) =>
        titleSortFilters({
          title: "Last lock at",
          icon: usersColumnIconMap["last_lock_at"],
          stateData: state,
          key: "last_lock_at"
        }),
      dataIndex: "last_lock_at",
      width: 185,
      render: (_) =>
        _ ? (
          <Space direction="vertical" size={4}>
            <Text>{dayjs(_).format(dateFormat)}</Text>
            <Text type="secondary">{dayjs(_).format("h:mm A [(UTC)]")}</Text>
          </Space>
        ) : (
          "—"
        ),
      onCell: (record, index) => onCell(record, index, userIdList, data)
    },
    last_active: {
      key: "last_active",
      title: (state) =>
        titleSortFilters({
          title: "Last activity",
          icon: alertsColumnIconMap["last_active"],
          stateData: state,
          key: "last_active"
        }),
      dataIndex: "last_active",
      width: 185,
      render: (_, { business_account }) => {
        let date = business_account?.last_active
        if (!date) return "—"
        return (
          <Space direction="vertical" size={4}>
            <Text>{dayjs(date).format(dateFormat)}</Text>
            <Text type="secondary">{dayjs(date).format("h:mm A [(UTC)]")}</Text>
          </Space>
        )
      }
    },
    categories: {
      key: "categories",
      title: (state) =>
        titleSortFilters({
          title: "User type",
          icon: alertsColumnIconMap["categories"],
          stateData: state,
          key: "categories"
        }),
      width: 200,
      dataIndex: "categories",
      render: (_, record) => {
        const text = _?.length ? _?.join(", ") : "—"

        if (_?.includes("Loyal billing")) return <Text>{text}</Text>

        return (
          <Dropdown
            destroyPopupOnHide={true}
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "switch_to_loyal_billing",
                  label: "Switch to Loyal Billing"
                }
              ],
              onClick: ({ key, domEvent }) => {
                domEvent?.stopPropagation()
                onSelectRow(record, key)
              }
            }}
            placement="bottomRight"
          >
            <Flex gap={4} align="center">
              <Text>{text}</Text>
              <Icon type="chevron_down_small" />
            </Flex>
          </Dropdown>
        )
      }
    },
    credit_automation: {
      key: "credit_automation",
      title: (state) =>
        titleSortFilters({
          title: "Credit automation",
          icon: usersColumnIconMap["credit_automation"],
          stateData: state,
          key: "credit_automation"
        }),
      dataIndex: "credit_automation_access",
      width: 200,
      // credit_automation_access is per-user, so merge the user's business-account rows into a
      // single cell (one toggle per user) like the other user-level columns above.
      onCell: (record, index) => onCell(record, index, userIdList, data),
      render: (_, record) => {
        // Loyalty users always have access and can't be toggled (backend returns is_credit_automation_locked).
        const locked = !!record?.is_credit_automation_locked
        const checked = locked ? true : !!record?.credit_automation_access
        return (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <span onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={checked}
              disabled={locked}
              onChange={(next) =>
                onSelectRow(record, next ? "enable_credit_automation" : "disable_credit_automation")
              }
            />
          </span>
        )
      }
    },
    auto_top_up: {
      key: "auto_top_up",
      title: (state) =>
        titleSortFilters({
          title: "Auto top-up",
          icon: usersColumnIconMap["auto_top_up"],
          stateData: state,
          key: "auto_top_up"
        }),
      dataIndex: "auto_top_up",
      width: 140,
      render: (_) => (_ ? "On" : "Off"),
      onCell: (record, index) => onCell(record, index, userIdList, data)
    },
    ba_status: {
      key: "ba_status",
      title: (state) =>
        titleSortFilters({
          title: "BA Status",
          icon: alertsColumnIconMap["ba_status"],
          stateData: state,
          key: "ba_status"
        }),
      dataIndex: "ba_status",
      width: 120,
      render: (_, { business_account }) => getStatusTag(business_account?.ba_status)
    },
    role: {
      key: "role",
      title: (state) =>
        titleSortFilters({
          title: "Role",
          icon: alertsColumnIconMap["role"],
          stateData: state,
          key: "role"
        }),
      dataIndex: "role",
      width: 120,
      render: (_, { business_account }) => business_account?.role?.name || "—"
    },
    role_status: {
      key: "role_status",
      title: (state) =>
        titleSortFilters({
          title: "Role status",
          icon: alertsColumnIconMap["role_status"],
          stateData: state,
          key: "role_status"
        }),
      dataIndex: "role_status",
      width: 140,
      render: (_, { business_account }) => getStatusTag(business_account?.role?.role_status)
    },
    balance_current: {
      key: "balance_current",
      title: (state) =>
        titleSortFilters({
          title: "Current Balance",
          icon: alertsColumnIconMap["balance_current"],
          stateData: state,
          key: "balance_current",
          tooltip: "The balance is shared across all business accounts"
        }),
      dataIndex: "balance_current",
      width: 180,
      //render: (_) => _ >= 0 ? ('$' + formatNumber(_, 2, ',')) : ('-$' + formatNumber(Math.abs(_), 2, ',')),
      render: (_, record) => (
        <ButtonComponent
          type="link"
          shape="circle"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onSelectRow(record, "user_balance")
          }}
          className="flex center"
        >
          {_ >= 0 ? "$" + formatNumber(_, 2, ",") : "-$" + formatNumber(Math.abs(_), 2, ",")}
        </ButtonComponent>
      )
    },
    balance_usage: {
      key: "balance_usage",
      title: (state) =>
        titleSortFilters({
          title: "Balance usage",
          icon: alertsColumnIconMap["balance_usage"],
          stateData: state,
          key: "balance_usage"
        }),
      dataIndex: "balance_usage",
      minWidth: 150,
      render: (_) => (_ ? "Yes" : "No")
    },
    alerts_in_debt_count: {
      key: "alerts_in_debt_count",
      sorter: true,
      title: (state) =>
        titleSortFilters({
          title: "Alerts in Debt",
          sorter: true,
          icon: alertsColumnIconMap["alerts_in_debt_count"],
          stateData: state,
          key: "alerts_in_debt_count"
        }),
      dataIndex: "alerts_in_debt_count",
      width: 160
    }
  }
}

export const getDefaultColumn = ({ field_id, label, t }) => ({
  key: field_id,
  sorter: true,
  title: (state) =>
    titleSortFilters({
      title: t(label),
      sorter: true,
      icon: alertsColumnIconMap[field_id],
      stateData: state,
      key: field_id
    }),
  dataIndex: field_id,
  minWidth: 160,
  ellipsis: true,
  render: (_) => _ || "—"
})

const dropDownItems = [
  {
    key: "withdraw",
    label: "Withdraw"
  },
  {
    key: "gift",
    label: "Gift"
  },
  {
    key: "delete",
    label: "Delete"
  },
  {
    key: 'settings',
    label: 'Settings',
  },
  {
    key: 'comments_users_page',
    label: 'Comments',
  },
]

const canGiftUser = (record) => {
  const userStatus = record?.status?.toLowerCase()
  const userRoleName = record?.role?.name
  const businessAccountRoleName = record?.business_account?.role?.name
  const hasBusinessAccount = Boolean(record?.business_account)

  if (userStatus !== "active") {
    return false
  }

  if (userRoleName === "Owner" || businessAccountRoleName === "Owner") {
    return true
  }

  return (
    ["Administrator", "Manager"].includes(userRoleName) &&
    hasBusinessAccount &&
    businessAccountRoleName === "Owner"
  )
}

export const getActionColumn = ({ onSelectRow, t, data }) => {
  const userIdList = data ? [...data]?.map(({ id }) => id) : null
  return {
    key: "action",
    title: "Actions",
    width: 118,
    align: "center",
    onCell: data ? (record, index) => onCell(record, index, userIdList, data) : undefined,
    render: (_, record) => (
      <Dropdown
        destroyPopupOnHide={true}
        menu={{
          items: dropDownItems.map((item) => {
            const baseItem = {
              ...item,
              label: t(item.label)
            }

            switch (item.key) {
              case "withdraw":
                return { ...baseItem, disabled: !record?.is_unenrolled }
              case "gift":
                return { ...baseItem, disabled: !canGiftUser(record) }
              case "delete":
                return { ...baseItem, disabled: !record?.is_deletable }
              default:
                return baseItem
            }
          }),
          onClick: ({ key, domEvent }) => {
            domEvent?.stopPropagation()
            onSelectRow(record, key)
          }
        }}
        placement="bottomRight"
      >
        <Flex justify="center">
          <Icon type="three_dots" style={{ transform: "rotate(0deg)" }} />
        </Flex>
      </Dropdown>
    )
  }
}
